import { Component } from '@angular/core';
import { finalize } from 'rxjs';

import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Composants
import { PageBaseComponent } from 'src/app/@shared/components/abstract/page-base.component';
import { AssistantAuthorizationFormComponent } from '../../components/assistant-authorization-form/assistant-authorization-form.component';
import { LegalFooterComponent } from 'src/app/legal/components/legal-footer/legal-footer.component';
import { MessageComponent } from 'src/app/@shared/components/message/message.component';

// Modèles
import { AssistantAuthorizationRequest } from 'src/app/@shared/models/interfaces/assistant-authorization-request.model';

/**
 * Page d'autorisation d'un assistant IA (#1312), route `/oauth/authorize`.
 *
 * L'assistant y envoie l'utilisateur avec sa demande dans l'URL. La page est hors
 * dossier et hors du shell : elle vit derrière `AuthenticationGuard`, qui conserve
 * l'URL complète le temps de la connexion. Elle fait contrôler la demande par le
 * serveur, puis délègue le choix du dossier au formulaire.
 */
@Component({
    selector: 'app-assistant-authorization',
    imports: [
        ProgressSpinnerModule,
        AssistantAuthorizationFormComponent,
        LegalFooterComponent,
        MessageComponent
    ],
    templateUrl: './assistant-authorization.component.html',
    styleUrls: ['./assistant-authorization.component.scss']
})
export class AssistantAuthorizationComponent extends PageBaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    /** La demande a été refusée par le serveur : la page ne peut pas aboutir. */
    public refused: boolean = false;

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    public authorizationRequest: AssistantAuthorizationRequest | null = null;

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override loadData(): void {
        this._loadRequest();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Fait contrôler la demande par le serveur. Les paramètres de l'URL lui sont transmis
     * tels quels ; ceux que l'autorisation renverra (`redirect_uri`, défi PKCE, `state`)
     * sont reportés sur la demande, le serveur ne les servant pas.
     */
    private _loadRequest(): void {
        const params: { [key: string]: string } = {};
        for (const key of this.queryParams.keys) {
            params[key] = this.queryParams.get(key)!;
        }

        this.loading = true;
        this.assistantAuthorizationService.getRequest$(params).pipe(
            finalize(() => this.loading = false)
        ).subscribe((authorizationRequest: AssistantAuthorizationRequest | null) => {
            if (!authorizationRequest) {
                this.refused = true;
                return;
            }
            authorizationRequest.redirectUri = params['redirect_uri'];
            authorizationRequest.codeChallenge = params['code_challenge'];
            authorizationRequest.codeChallengeMethod = params['code_challenge_method'];
            authorizationRequest.state = params['state'] ?? null;
            this.authorizationRequest = authorizationRequest;
        });
    }
}
